import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Box, colors, Stack, useMediaQuery, useTheme } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GameContext } from '../../../contexts/GameContext';
import ActionBar from '../ActionBar/ActionBar';
import Card from '../Cards/Card';
import { TrackDto } from '../../../Dtos/TrackDto';

const pulseKeyframes = {
    '@keyframes pulse': {
        '0%, 100%': { opacity: 0.4 },
        '50%': { opacity: 1 },
    },
};

const arrowSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    color: 'white',
    fontSize: { xs: 40, sm: 72 },
    cursor: 'pointer',
    animation: 'pulse 2s ease-in-out infinite',
    zIndex: 2,
    '&:hover': { opacity: 1 },
} as const;

const GameBoard = () => {
    const gameContext = useContext(GameContext);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const cardSize = isMobile ? '95px' : '250px';

    // Références pour le drag to scroll
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    // Spécifie le bon type pour cards
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);

    const [scrollRatio, setScrollRatio] = useState(0);
    const [thumbRatio, setThumbRatio] = useState(1);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const updateScrollIndicators = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 1);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
        const maxScroll = el.scrollWidth - el.clientWidth;
        setScrollRatio(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
        setThumbRatio(el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1);
        setIsScrolling(true);
    }, []);

    useEffect(() => {
        updateScrollIndicators();
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateScrollIndicators);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateScrollIndicators, gameContext.currentTurn?.turnHolder.cards]);

    // Gestion des événements de glisser
    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        clearTimeout(scrollTimeoutRef.current);
        setIsScrolling(true);
        startXRef.current = e.pageX - (scrollRef.current?.offsetLeft ?? 0);
        scrollLeftRef.current = scrollRef.current?.scrollLeft ?? 0;
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 300);
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 300);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = (x - startXRef.current) * 1; // Ajuste la vitesse du glissement
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
        }
    };

    const scrollByDirection = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = el.clientWidth * 0.8;
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    return (
        <Stack
            sx={{
                ...pulseKeyframes,
                justifyContent: 'flex-start',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                padding: { xs: '0 4px', sm: '0 24px', md: '0 40px' },
                boxSizing: 'border-box',
                display: 'flex',
                position: 'relative',
                color: "red"
            }}
        >
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', flexShrink: 0, paddingTop: { xs: '8px', sm: '16px' } }}>
                <ActionBar />
            </Box>

            <Box sx={{ position: 'relative', width: '100%', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
                {canScrollLeft && (
                    <ChevronLeftIcon sx={{ ...arrowSx, left: -4 }} onClick={() => scrollByDirection('left')} />
                )}
                {canScrollRight && (
                    <ChevronRightIcon sx={{ ...arrowSx, right: -4 }} onClick={() => scrollByDirection('right')} />
                )}
                <Stack
                    direction={'row'}
                    ref={scrollRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onScroll={updateScrollIndicators}
                    sx={{
                        height: 'auto',
                        width: '100%',
                        flexWrap: 'nowrap',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        justifyContent: 'safe center',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' }
                    }}
                >
                    {gameContext.currentTurn?.turnHolder.cards.map((card: TrackDto, index: number) => (
                        <Card
                            key={`${card.artist}_${card.title}`}
                            singer={card.artist}
                            title={card.title}
                            year={card.year!}
                            width={cardSize}
                            height={cardSize}
                            isIcon={false}
                            doubleFreeSpot={index === 0}
                        />
                    ))}
                </Stack>
                {thumbRatio < 1 && (
                    <Box sx={{
                        width: { xs: '90%', sm: 700 },
                        maxWidth: '100%',
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        margin: '10px auto 0',
                        position: 'relative',
                        overflow: 'hidden',
                        opacity: isScrolling ? 1 : 0.3,
                        transition: isScrolling ? 'opacity 0s' : 'opacity 2s ease-out',
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${scrollRatio * (1 - thumbRatio) * 100}%`,
                            width: `${thumbRatio * 100}%`,
                            height: '100%',
                            borderRadius: 2,
                            backgroundColor: isScrolling ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
                            transition: isScrolling
                                ? 'left 0.05s ease-out, background-color 0s'
                                : 'left 0.05s ease-out, background-color 2s ease-out',
                        }} />
                    </Box>
                )}
            </Box>
        </Stack>
    );
};

export default GameBoard;
